import {Injectable} from '@angular/core';
import {GatewayAccountService, UserDTO} from '@tj/tj-dmp-lib-api';
import {BehaviorSubject, Observable} from 'rxjs';
import {ANY_ADMIN, isAnyAdmin} from './admin-levels';

@Injectable({
    providedIn: 'root'
})
export class AccountService {
    private userIdentity: UserDTO;
    private authenticated = false;
    private authenticationState = new BehaviorSubject<UserDTO>(null);

    constructor(
        private gatewayAccountService: GatewayAccountService,
    ) {
    }

    fetch(): Observable<UserDTO> {
        return this.gatewayAccountService.getAccountUsingGET();
    }

    getAuthenticationState(): Observable<UserDTO> {
        return this.authenticationState.asObservable();
    }

    isAuthenticated(): boolean {
        return this.authenticated;
    }

    identity(force?: boolean): Promise<UserDTO> {
        if (force) {
            this.userIdentity = undefined;
        }

        // check and see if we have retrieved the userIdentity data from the server.
        // if we have, reuse it by immediately resolving
        if (this.userIdentity) {
            return Promise.resolve(this.userIdentity);
        }

        // retrieve the userIdentity data from the server, update the identity object, and then resolve.
        return this.fetch()
            .toPromise()
            .then(response => {
                const account: UserDTO = response;
                if (account && account.id) {
                    if (!account.authorities) {
                        account.authorities = [];
                    }
                    this.userIdentity = account;
                    this.authenticated = true;
                    // this.trackerService.connect();
                } else {
                    this.userIdentity = null;
                    this.authenticated = false;
                }
                this.authenticationState.next(this.userIdentity);
                return this.userIdentity;
            })
            .catch(() => {
                this.userIdentity = null;
                this.authenticated = false;
                this.authenticationState.next(this.userIdentity);
                return null;
            });
    }

    hasAnyAuthority(authorities: string[]): boolean {
        if (!this.authenticated || !this.userIdentity || !this.userIdentity.authorities) {
            return false;
        }
        console.log('authorities: ', this.userIdentity.authorities);

        return this.userIdentity.authorities.includes('ROLE_ADMIN')
            || authorities.some((authority: string) => this.userIdentity.authorities.includes(authority));
    }

    hasAnyMenu(menus: string[]): boolean {
        if (!this.authenticated || !this.userIdentity || !this.userIdentity.authorities) {
            return false;
        }
        const menusUser = [];
        if (this.userIdentity.rules) {
            menusUser.push(...this.userIdentity.rules.map(r =>
                r.rule.replace('READ_', '').replace('EDIT_', '').replace('WRITE_', '')
            ));
        }
        return this.userIdentity.authorities.includes('ROLE_ADMIN')
            || (menus.includes(ANY_ADMIN) && isAnyAdmin(this.userIdentity))
            || menus.some((rule: string) => menusUser.includes(rule));
    }

    hasAnyPermission(permissions: string[]): boolean {
        if (!this.authenticated || !this.userIdentity) {
            return false;
        }
        if (this.userIdentity.admin) {
            return true;
        }
        if (permissions.includes(ANY_ADMIN) && isAnyAdmin(this.userIdentity)) {
            return true;
        }
        const userPermissions = this.userIdentity.rules?.map(p => p.rule) || [];
        return permissions.some((permission:string) => userPermissions.includes(permission))
    }

    isSuperAdmin(): boolean {
        return this.authenticated && this.userIdentity?.admin === true;
    }

}
